import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import type { AxiosResponse } from 'axios';
import { useNotification } from 'hooks/useNotification';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import {
  forceCleanUpSubscriptionApi,
  ForceCleanUpSubscriptionErrorResponse,
  ForceCleanUpSubscriptionParams,
  ForceCleanUpSubscriptionSuccessResponse,
} from 'RealmModule/api/forceCleanUpSubscriptionApi';
import { realmQueryKeys } from 'RealmModule/query/realmQueryKeys';

export const useForceCleanUpSubscriptionMutation = ({
  onSuccess,
}: {
  /**
   * Registered at the mutation (not observer) level so it still fires when the
   * confirmation modal has unmounted while the request was in flight.
   */
  onSuccess?: VoidFunction;
} = {}) => {
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<ForceCleanUpSubscriptionSuccessResponse>,
    AxiosResponse<ForceCleanUpSubscriptionErrorResponse>,
    ForceCleanUpSubscriptionParams
  >({
    mutationFn: async (params) => {
      if (!token) {
        throw new Error('No auth token available');
      }
      return forceCleanUpSubscriptionApi({ params, token }) as Promise<
        AxiosResponse<ForceCleanUpSubscriptionSuccessResponse>
      >;
    },
    onSuccess: (response) => {
      showSuccess(response.data?.message || 'Successfully cleaned up subscription resources.');
      queryClient.invalidateQueries({ queryKey: realmQueryKeys.lists() });
      onSuccess?.();
    },
    onError: (error) => {
      showError(`Force clean up subscription failed! ${extractApiErrorMessage(error)}`);
    },
  });

  return {
    forceCleanUpSubscription: mutation.mutate,
    isForceCleaningUpSubscription: mutation.isPending,
  };
};
